import {
  ConstraintSku,
  GameArgsType,
  GameType,
  UserType,
  PlayerType,
  PlayerArgsType,
  PlayersType,
  PurchasesType,
  TopicPackSku,
} from "@/types";
import Api, { Data } from "@/app/api/Api";
import { Vue } from "vue-property-decorator";
import { version } from "@/config";
import { random } from "@/app/helpers";
import { COLORS } from "@/app/constants";
import LocalStore from "@/app/LocalStore";
import Engine from "@/app/engine/Engine";

type LocalKeys =
  | "checkoutId"
  | "currentGameId"
  | "disabledConstraints"
  | "disabledPacks"
  | "galleryMode"
  | "player";

export default class Store {
  api: Api;
  app: Vue;
  game: GameType | null;
  gameId?: string;
  disabledConstraints: ConstraintSku[];
  disabledPacks: TopicPackSku[];
  galleryMode: boolean;
  purchases: PurchasesType | null;
  user: UserType;

  static local: { [K in LocalKeys]: LocalStore } = {
    checkoutId: new LocalStore("checkoutId"),
    currentGameId: new LocalStore("currentGameId"),
    disabledConstraints: new LocalStore("disabledConstraints"),
    disabledPacks: new LocalStore("disabledPacks"),
    galleryMode: new LocalStore("galleryMode"),
    player: new LocalStore("player"),
  };

  constructor(app: Vue) {
    this.app = app;
    this.api = app.$root.$data.api;
    this.disabledConstraints = JSON.parse(
      Store.local.disabledConstraints.read() || "[]"
    ) as ConstraintSku[];
    this.disabledPacks = JSON.parse(Store.local.disabledPacks.read() || "[]") as TopicPackSku[];
    this.galleryMode = JSON.parse(Store.local.galleryMode.read() || "false");
    this.game = null;
    this.purchases = null;
    this.user = app.$root.$data.user;
  }

  initialize() {
    //
  }

  roundStart(game: GameType) {
    if (!game || !this.userIsGameOwner()) return;
    if (game.state === "lobby") {
      const newGame = Engine.freshRound(
        game,
        this.purchases,
        this.disabledPacks,
        this.disabledConstraints,
        this.galleryMode
      );
      this.gameUpdate(newGame, { merge: false });
    } else if (game.state === "results") {
      const newGame = Engine.freshRound(
        game,
        this.purchases,
        this.disabledPacks,
        this.disabledConstraints,
        this.galleryMode
      );
      this.gameUpdate(newGame, { merge: false });
    }
  }

  handleError(error: unknown) {
    console.error(error);
  }

  handleGameUpdate(data?: Data) {
    const gameData: GameType = data as GameType;
    if (!gameData && this.game) {
      Store.local.currentGameId.remove();
      this.syncGameToData(null);
    } else if (!gameData && !this.game) this.api.gameWrite(this.modelInitialGame());
    else if (!gameData?.players[this.user.uid] && Store.local.currentGameId.read())
      this.api.gameWrite({ players: this.modelAddToPlayers() });
    else if (this.userIsGameOwner()) {
      this.syncGameToData(gameData);
      if (!this.game) return;
      if (
        this.game.state === "responses" &&
        Object.keys(this.game.roles).length === Object.keys(this.game.responses).length
      ) {
        this.api.gameWrite({ state: "voting" });
      } else if (
        this.game.state === "voting" &&
        Object.keys(this.game.roles).length === Object.keys(this.game.votes).length
      ) {
        this.api.gameWrite(Engine.generateResults(gameData));
      }
    } else this.syncGameToData(gameData);
  }

  handlePurchasesUpdate(data?: Data) {
    const purchasesData: PurchasesType = data as PurchasesType;
    this.syncPurchasesToData(purchasesData);
  }

  gameInit(gameId: string, isOwner = true) {
    return new Promise((resolve, reject) => {
      this.gameId = gameId;
      const run = (shouldProceed: boolean) => {
        if (!shouldProceed) return reject("gameId is invalid");
        Store.local.currentGameId.set(gameId);
        resolve(
          this.api.gameInitialize(
            gameId,
            this.handleGameUpdate.bind(this),
            this.handleError.bind(this)
          )
        );
      };
      if (isOwner) run(true);
      else Api.Game.idExists(gameId).then(run);
    });
  }
  purchasesInit() {
    return this.api.purchasesInitialize(
      this.handlePurchasesUpdate.bind(this),
      this.handleError.bind(this)
    );
  }

  gameBoot(id: string) {
    if (!this.game) return;
    if (this.game.ownerId !== this.user.uid) return;
    if (!this.game.players[id]) return;
    return this.api.gameBoot(id);
  }
  gameDelete() {
    if (!this.game) return;
    if (this.game.ownerId !== this.user.uid) return;
    this.gameId = undefined;
    Store.local.currentGameId.remove();
    return this.api.gameDelete();
  }
  gameLeave() {
    if (!this.game) return;
    if (this.game.ownerId === this.user.uid) this.gameDelete();
    else this.api.gameLeave();
    Store.local.currentGameId.remove();
  }
  gameUpdate(game: GameArgsType, options = { merge: true }) {
    return this.api.gameWrite(game, options);
  }
  gameUpdatePlayer(player: PlayerArgsType) {
    this.gameUpdate({ players: { [this.user.uid]: player } });
    Store.local.player.set(JSON.stringify(player));
  }

  localPlayer() {
    const player = Store.local.player.read();
    if (!player) return {};
    return JSON.parse(player);
  }

  modelInitialGame(): GameArgsType {
    return {
      created: Date.now().toString(),
      id: this.gameId,
      ownerId: this.user.uid,
      state: "lobby",
      players: this.modelAddToPlayers(),
      responses: {},
      deaths: {},
      roles: {},
      scores: {},
      votes: {},
    };
  }
  modelAddToPlayers(): PlayersType {
    return { [this.user.uid]: this.modelInitialPlayer() };
  }
  randomName() {
    const names: string[] = ["buddy", "pal", "weasel", "martian"];
    return random(names);
  }
  modelInitialPlayer(): PlayerType {
    const local = this.localPlayer();
    const name = this.user.displayName
      ? this.user.displayName.toLowerCase().replace(/ /g, "")
      : this.randomName();
    const args: PlayerType = {
      id: this.user.uid,
      color: local.color || random(COLORS),
      tag: local.tag || name,
      version,
    };
    if (this.user.photoURL) args.avatar = this.user.photoURL;
    return args;
  }

  // eslint-disable-next-line
  syncPurchasesToData(purchasesData?: any) {
    if (!purchasesData) {
      this.app.$set(this.app.$root.$data, "purchases", null);
      delete this.purchases;
    } else if (!this.purchases) {
      this.app.$root.$data.purchases = purchasesData;
      this.purchases = purchasesData as PurchasesType;
    } else {
      for (const key in purchasesData) {
        if (typeof purchasesData[key] === "object") {
          for (const k1 in purchasesData[key]) {
            this.app.$set(this.app.$root.$data.purchases[key], k1, purchasesData[key][k1]);
            this.purchases = {
              ...this.purchases,
              [k1]: purchasesData[key][k1],
            } as PurchasesType;
          }
        } else {
          this.app.$set(this.app.$root.$data, "purchases", purchasesData);
          this.purchases = purchasesData;
        }
      }
    }
  }

  // eslint-disable-next-line
  syncGameToData(gameData?: any) {
    if (!gameData) {
      this.app.$set(this.app.$root.$data, "game", null);
      delete this.game;
    } else if (!this.game) {
      this.app.$root.$data.game = gameData;
      this.game = gameData as GameType;
    } else {
      for (const key in gameData) {
        if (typeof gameData[key] === "object") {
          for (const k1 in gameData[key]) {
            if (typeof gameData[key][k1] === "object") {
              for (const k2 in gameData[key][k1]) {
                this.app.$set(this.app.$root.$data.game[key][k1], k2, gameData[key][k1][k2]);
                if (!this.game) return;
                this.game = {
                  ...this.game,
                  [k1]: {
                    ...(this.game[k1 as keyof GameType] as GameArgsType),
                    [k2]: gameData[key][k1][k2],
                  },
                } as GameType;
              }
            } else {
              this.app.$set(this.app.$root.$data.game[key], k1, gameData[key][k1]);
              this.game = {
                ...this.game,
                [k1]: gameData[key][k1],
              } as GameType;
            }
          }
        } else {
          this.app.$set(this.app.$root.$data, "game", gameData);
          this.game = gameData;
        }
      }
    }
  }

  toggleConstraint(sku: ConstraintSku) {
    const index = this.disabledConstraints.indexOf(sku);
    if (index !== -1) this.disabledConstraints.splice(index, 1);
    else this.disabledConstraints.push(sku);
    Store.local.disabledConstraints.set(JSON.stringify(this.disabledConstraints));
  }
  toggleGalleryMode() {
    this.galleryMode = !this.galleryMode;
    Store.local.galleryMode.set(JSON.stringify(this.galleryMode));
  }

  toggleTopic(sku: TopicPackSku) {
    const index = this.disabledPacks.indexOf(sku);
    if (index !== -1) this.disabledPacks.splice(index, 1);
    else this.disabledPacks.push(sku);
    Store.local.disabledPacks.set(JSON.stringify(this.disabledPacks));
  }

  userIsGameOwner() {
    if (!this.game) return false;
    return this.game.ownerId === this.user.uid;
  }
}
